<script>
import AoikumoDisabledTooltip from 'dashboard/components/dahreply/AoikumoDisabledTooltip.vue';
import AoikumoIntegrationTag from 'dashboard/components/dahreply/AoikumoIntegrationTag.vue';

export default {
  name: 'StaffTable',
  components: {
    AoikumoDisabledTooltip,
    AoikumoIntegrationTag,
  },
  props: {
    staffList: {
      type: Array,
      required: true,
      default: () => [],
    },
    showActions: {
      type: Boolean,
      default: true
    },
    isBranch: {
      type: Boolean,
      default: false,
    },
    branchId: {
      type: String,
      default: null,
    }
  },
  emits: ['edit', 'delete', 'navigate', 'open-link-modal'],
  computed: {
    metadataKeys() {
      const keys = new Set();
      this.staffList.forEach(item => {
        const metadata = item.staff?.staff_metadata;
        if (metadata && typeof metadata === 'object') {
          Object.keys(metadata).forEach(k => keys.add(k));
        }
      });
      return Array.from(keys);
    },
  },
  methods: {
    getBranchStaffServices(staff) {
      if (!this.branchId) return [];
      return staff.services?.filter(s => 
        s.branches && s.branches.some(b => b.id === this.branchId)
      ) || [];
    },
    getBranchStaffServicesText(staff) {
      const services = this.getBranchStaffServices(staff);
      if (services.length === 0) return 'Not assigned';
      return services.map(s => s.name).join(', ');
    },
    handleOpenLinkModal(staff) {
      const services = this.getBranchStaffServices(staff);
      this.$emit('open-link-modal', {
        staffId: staff.staff.id,
        services: services,
      });
    }
  }
};
</script>

<template>
  <div class="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-auto">
    <table class="min-w-full text-sm">
      <thead
        class="bg-slate-50 dark:bg-slate-700/50 text-left text-slate-600 dark:text-slate-300 uppercase text-xs font-semibold"
      >
        <tr>
          <th class="px-4 py-3">Name</th>
          <th class="px-4 py-3">Status</th>

          <!-- Dynamic staff_metadata headers -->
          <th
            v-for="key in metadataKeys"
            :key="key"
            class="px-4 py-3 capitalize"
          >
            {{ key.replace(/_/g, ' ') }}
          </th>

          <!-- Branch Services column when in branch view -->
          <th v-if="isBranch" class="px-4 py-3">Branch Services</th>

          <th class="px-4 py-3 text-right">Actions</th>
        </tr>
      </thead>

      <tbody>
        <!-- Empty State -->
        <tr v-if="!staffList.length">
          <td
            :colspan="5 + metadataKeys.length"
            class="text-center py-6 text-slate-500 dark:text-slate-400"
          >
            No staff found.
          </td>
        </tr>

        <!-- Rows -->
        <tr
          v-for="staff in staffList"
          :key="staff.staff.id || staff.staff.staff_id"
          class="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors cursor-pointer"
          @click="$emit('navigate', staff.staff.id || staff.staff.staff_id)"
        >
          <td class="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">
            {{ staff.staff.name }}
            <AoikumoIntegrationTag :item="staff.staff" class="ml-2" />
          </td>
          <td class="px-4 py-3">
            <span
              :class="[ 
                'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium',
                staff.staff.is_active
                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                  : 'bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100'
              ]"
            >
              <fluent-icon
                :icon="staff.staff.is_active ? 'checkmark-circle' : 'warning'"
                class="h-3 w-3 mr-1"
              />
              {{ staff.staff.is_active ? 'Active' : 'Inactive' }}
            </span>
          </td>

          <!-- Dynamic metadata values -->
          <td
            v-for="key in metadataKeys"
            :key="key"
            class="px-4 py-3"
          >
            {{ staff.staff.staff_metadata ? staff.staff.staff_metadata[key] : '-' }}
          </td>

          <!-- Branch Services column when in branch view -->
          <td v-if="isBranch" class="px-4 py-3">
            <div class="flex items-center gap-2">
              <span 
                class="text-slate-700 dark:text-slate-300 text-sm truncate max-w-xs"
                :title="getBranchStaffServicesText(staff)"
              >
                {{ getBranchStaffServicesText(staff) }}
              </span>
              <button
                @click.stop="handleOpenLinkModal(staff)"
                class="p-1 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-opacity-50 flex-shrink-0"
                aria-label="Link services"
                title="Link services"
              >
                <fluent-icon icon="edit" class="h-4 w-4" />
              </button>
            </div>
          </td>

          <!-- Actions -->
          <td class="px-4 py-3 text-right flex justify-end gap-2">
            <AoikumoDisabledTooltip :disabled="!showActions" class="inline-flex gap-2">
              <woot-button
                size="small"
                variant="smooth"
                color-scheme="secondary"
                icon="edit"
                :is-disabled="!showActions"
                @click.stop="$emit('edit', staff.staff)"
              />
              <woot-button
                size="small"
                variant="smooth"
                color-scheme="alert"
                icon="delete"
                :is-disabled="!showActions"
                @click.stop="$emit('delete', staff.staff)"
              />
            </AoikumoDisabledTooltip>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
